import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getModelAccessories, hasModelAccessories, getModelParticular, getModelDefaults } from '../config/models';

const ProductSpecs = ({ formData, updateFormData, nextStep, prevStep, currentStep, totalSteps }) => {
  const [editMode, setEditMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const previousModelRef = useRef(null);

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
    // Clear validation error when user selects a value
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Get current model code (check both model and modelNo fields)
  const currentModelCode = useMemo(() => {
    return formData.model || formData.modelNo || null;
  }, [formData.model, formData.modelNo]);

  // Auto-populate particular and accessories fields based on model configuration
  useEffect(() => {
    if (!currentModelCode) {
      // No model selected, clear particular and accessories
      if (formData.particular) {
        updateFormData({ particular: "" });
      }
      if (formData.accessories) {
        updateFormData({ accessories: "" });
      }
      if (formData.capillaryLength) {
        updateFormData({ capillaryLength: "" });
      }
      return;
    }

    // Auto-populate particular field
    const modelParticular = getModelParticular(currentModelCode);
    if (modelParticular && formData.particular !== modelParticular) {
      updateFormData({ particular: modelParticular });
    }

    // Clear accessories field when model changes (user must select from dropdown)
    const hasAccessories = hasModelAccessories(currentModelCode);
    const previousModel = previousModelRef.current;
    
    // Check if model has changed (before updating the ref)
    const modelChanged = previousModel !== null && previousModel !== currentModelCode;
    
    // Clear accessories if:
    // 1. Model doesn't support accessories, or
    // 2. Model changed (switched from one model to another)
    if (modelChanged) {
      // Model changed, clear accessories
      updateFormData({ accessories: "" });
    } else if (!hasAccessories && formData.accessories) {
      // Model doesn't support accessories, clear it
      updateFormData({ accessories: "" });
    }
    
    // Update the previous model reference
    previousModelRef.current = currentModelCode;

    // Auto-populate specification fields from model defaults
    // Strategy:
    // 1. If model changed: reset all fields to defaults (user is switching models)
    // 2. If model unchanged: only populate empty fields (preserve user edits)
    const modelDefaults = getModelDefaults(currentModelCode);
    if (modelDefaults) {
      const updates = {};
      Object.keys(modelDefaults).forEach((field) => {
        const fieldValue = formData[field];
        const defaultValue = modelDefaults[field];
        const isEmpty = !fieldValue || fieldValue === '';
        
        if (modelChanged) {
          // Model changed: reset to defaults (user is switching to a different model)
          if (fieldValue !== defaultValue) {
            updates[field] = defaultValue;
          }
        } else if (isEmpty) {
          // Model unchanged but field is empty: populate with default (initial load or user cleared field)
          updates[field] = defaultValue;
        }
        // If model unchanged and field has a value, preserve it (user has edited it)
      });
      if (Object.keys(updates).length > 0) {
        updateFormData(updates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModelCode]);

  const showAccessories = hasModelAccessories(currentModelCode);
  const accessoriesOptions = [
    { value: '', label: 'Select an option' },
    { value: 'External CCU', label: 'External CCU' },
    { value: 'External CCU and RI', label: 'External CCU and RI' },
    { value: 'External CCU and RI (1 O/P)', label: 'External CCU and RI (1 O/P)' },
    { value: 'External CCU and RI (2 O/P)', label: 'External CCU and RI (2 O/P)' },
    { value: 'External CCU and RI (4 O/P)', label: 'External CCU and RI (4 O/P)' },
  ];

  const validateAccessories = () => {
    const errors = {};
    if (showAccessories && (!formData.accessories || formData.accessories === '')) {
      errors.accessories = 'Accessories is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateAccessories()) {
      nextStep();
    }
  };

  return (
    <div className="form-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>Product Specifications</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', color: editMode ? '#61dafb' : '#6c757d', fontWeight: editMode ? 'bold' : 'normal' }}>
            {editMode ? 'Edit Mode: Enabled' : 'Edit Mode: Disabled'}
          </span>
          <label
            style={{
              position: 'relative',
              display: 'inline-block',
              width: '56px',
              height: '28px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={editMode}
              onChange={() => setEditMode(!editMode)}
              style={{
                opacity: 0,
                width: 0,
                height: 0,
              }}
            />
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: editMode ? '#61dafb' : '#6c757d',
                borderRadius: '28px',
                transition: 'all 0.3s ease',
                boxShadow: editMode 
                  ? '0 0 8px rgba(97, 218, 251, 0.5)' 
                  : 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  content: '""',
                  height: '22px',
                  width: '22px',
                  left: editMode ? '30px' : '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                }}
              />
            </span>
          </label>
        </div>
      </div>
      
      {/* Left Side Fields */}
      <div className="form-row">
        <div className="form-group">
          <label>Model:</label>
          <input
            type="text"
            value={formData.modelNo || ''}
            readOnly
            placeholder="Auto-populated from Customer Information"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>
        <div className="form-group">
          <label>Particular:</label>
          <input
            type="text"
            value={formData.particular || ''}
            readOnly
            placeholder="Auto-populated based on model"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Range:</label>
          <input
            type="text"
            value={formData.range || ''}
            onChange={(e) => handleInputChange('range', e.target.value)}
            readOnly={!editMode}
            placeholder="e.g., 0 - 150 °C"
            style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
          />
        </div>
        <div className="form-group">
          <label>No. of Switches:</label>
          <input
            type="text"
            value={formData.noOfSwitches || ''}
            readOnly
            placeholder="Fixed value: 2"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Dial Graduation:</label>
          <input
            type="text"
            value={formData.dialGraduation || ''}
            onChange={(e) => handleInputChange('dialGraduation', e.target.value)}
            readOnly={!editMode}
            placeholder="e.g., 2 °C"
            style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
          />
        </div>
        <div className="form-group">
          <label>Switch Type:</label>
          <input
            type="text"
            value={formData.switchType || ''}
            onChange={(e) => handleInputChange('switchType', e.target.value)}
            readOnly={!editMode}
            placeholder="e.g., Micro Switch"
            style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Accuracy:</label>
          <input
            type="text"
            value={formData.accuracy || ''}
            onChange={(e) => handleInputChange('accuracy', e.target.value)}
            readOnly={!editMode}
            placeholder="e.g., 1.5% of FSD"
            style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
          />
        </div>
        <div className="form-group">
          <label>Switch Rating:</label>
          <input
            type="text"
            value={formData.switchRating || ''}
            onChange={(e) => handleInputChange('switchRating', e.target.value)}
            readOnly={!editMode}
            placeholder="e.g., 5A @ 250VAC & 0.5A @ 250V DC"
            style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Capillary Length:</label>
          <input
            type="text"
            value={formData.capillaryLength || ''}
            onChange={(e) => handleInputChange('capillaryLength', e.target.value)}
            placeholder="Enter length in meters"
          />
        </div>
        <div className="form-group">
          <label>Switch Differential:</label>
          <input
            type="text"
            value={formData.switchDifferential || ''}
            onChange={(e) => handleInputChange('switchDifferential', e.target.value)}
            readOnly={!editMode}
            placeholder="e.g., 4 ± 2°C"
            style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: '1 1 100%', maxWidth: '100%' }}>
          <label>Degree of Protection:</label>
          <input
            type="text"
            value={formData.degreeOfProtection || ''}
            readOnly
            placeholder="Auto-populated: IP-55"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>
      </div>

      {/* Accessories field - only shown when model has accessories configured */}
      {showAccessories && (
        <div className="form-row">
          <div className="form-group" style={{ flex: '1 1 100%', maxWidth: '100%' }}>
            <label>Accessories: <span className="required">*</span></label>
            <div className="input-wrapper">
              <select
                value={formData.accessories || ''}
                onChange={(e) => handleInputChange('accessories', e.target.value)}
                className={validationErrors.accessories ? 'error' : ''}
                style={{ 
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              >
                {accessoriesOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {validationErrors.accessories && (
                <span className="error-message">{validationErrors.accessories}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WTI-specific fields - only shown for WTI models (4031-WTI, 4031H-WTI) */}
      {(currentModelCode === '4031-WTI' || currentModelCode === '4031H-WTI') && (
        <>
          <div className="form-row">
            <div className="form-group">
              <label>Gradient:</label>
              <select
                value={formData.gradient || 'Standard (100%Loading)'}
                onChange={(e) => handleInputChange('gradient', e.target.value)}
                readOnly={!editMode}
                disabled={!editMode}
                style={!editMode ? { 
                  backgroundColor: '#f5f5f5', 
                  cursor: 'not-allowed',
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                } : {
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              >
                <option value="Standard (100%Loading)">Standard (100%Loading)</option>
                <option value="120% loading">120% loading</option>
              </select>
            </div>
            <div className="form-group">
              <label>Current in Amps:</label>
              <input
                type="number"
                value={formData.currentInAmps || '5'}
                onChange={(e) => handleInputChange('currentInAmps', e.target.value)}
                readOnly={!editMode}
                placeholder="5"
                style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Winding Temperature in ℃:</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  value={formData.windingTemperature || '15'}
                  onChange={(e) => handleInputChange('windingTemperature', e.target.value)}
                  readOnly={!editMode}
                  placeholder="5 to 35"
                  style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  className={formData.windingTemperature && (parseFloat(formData.windingTemperature) < 5 || parseFloat(formData.windingTemperature) > 35) ? 'error' : ''}
                />
                {formData.windingTemperature && (parseFloat(formData.windingTemperature) < 5 || parseFloat(formData.windingTemperature) > 35) && (
                  <span className="error-message">Value must be between 5 and 35</span>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Requested Temperature Rise over Top oil in ℃:</label>
              <input
                type="number"
                value={formData.requestedTemperatureRise || '2'}
                onChange={(e) => handleInputChange('requestedTemperatureRise', e.target.value)}
                readOnly={!editMode}
                placeholder="2"
                style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>
        </>
      )}

      <div className="navigation-buttons">
        <button 
          className="btn btn-secondary" 
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          Previous
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNextStep}
          disabled={currentStep === totalSteps}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ProductSpecs;
